import { lazy, useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Eyebrow, PrismLight, Segments } from '@/components/ui'
import { markets, type MarketId } from '@/content/site'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { cx } from '@/lib/cx'
import { DURATION, gsap, ScrollTrigger, SplitText, useGSAP } from '@/lib/gsap'
import { useAppStore } from '@/lib/store'
import { SceneCanvas } from '@/webgl/SceneCanvas'
import { usePrefetchAfterIntro } from '@/webgl/prefetch'
import { withRetry } from '@/webgl/retry'
import type { GlobeControl } from './GlobeScene'
import styles from './Markets.module.css'

// d3-geo, topojson and the land data only load with the globe, near the viewport.
const loadGlobeScene = () => import('./GlobeScene')
const GlobeScene = lazy(() => withRetry(loadGlobeScene))
/** After the intro: fetch the globe and rasterise its land mask in idle time. */
const warmGlobe = () =>
  Promise.all([loadGlobeScene(), import('./land').then((module) => module.loadLandMask())])

const COMPACT_QUERY = '(max-width: 1023px)'
const BY_ID = new Map(markets.list.map((market) => [market.id as MarketId, market]))

/**
 * "India first. Then everyone else." (#markets). A white porcelain-glass globe with slate
 * land dots, iris beacons and arcs from Raipur drawn wave by wave as you scroll (a short
 * pin on wide screens). Drag to spin it; hover or focus a market in the list and the globe
 * turns to face it while its beacon blooms. Reduced motion: one composed still, all arcs
 * drawn.
 */
export function Markets() {
  usePrefetchAfterIntro(warmGlobe)
  const sectionRef = useRef<HTMLElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const globeRef = useRef<HTMLDivElement>(null)
  const tagRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef(new Map<MarketId, HTMLButtonElement>())
  const progress = useRef(0)
  const control = useRef<GlobeControl>({
    dragging: false,
    dragX: 0,
    dragY: 0,
    velocity: 0,
    hovered: null,
    pinned: null,
  })
  const drag = useRef({ id: -1, x: 0, y: 0, t: 0 })

  const reducedMotion = useReducedMotion()
  const compact = useMediaQuery(COMPACT_QUERY)
  const introStarted = useAppStore((state) => state.phase !== 'boot')
  const [pinned, setPinned] = useState<MarketId | null>(null)
  const [tagId, setTagId] = useState<MarketId | null>(null)
  const tagMarket = tagId ? BY_ID.get(tagId) : undefined

  // Scroll progress drives the waves: pinned on wide screens, a pass-through on phones.
  useGSAP(
    () => {
      if (!trackRef.current) return
      const trigger = ScrollTrigger.create({
        trigger: compact ? globeRef.current : trackRef.current,
        start: compact ? 'top 80%' : 'top top',
        end: compact ? 'bottom 35%' : 'bottom bottom',
        onUpdate: (self) => {
          progress.current = self.progress
        },
        onRefresh: (self) => {
          progress.current = self.progress
        },
      })
      progress.current = trigger.progress
    },
    { scope: sectionRef, dependencies: [compact] },
  )

  useGSAP(
    () => {
      if (!introStarted) return
      if (reducedMotion) {
        gsap.from('[data-reveal]', { opacity: 0, duration: 0.3 })
        return
      }
      const title = sectionRef.current?.querySelector<HTMLElement>('[data-title]')
      const trigger = { trigger: sectionRef.current, start: 'top 75%', once: true }
      gsap.from('[data-reveal]', {
        // Market buttons must remain reachable before scrolling reveals the list.
        opacity: 0,
        y: 16,
        duration: DURATION.cinematic,
        stagger: 0.07,
        scrollTrigger: trigger,
      })
      if (title) {
        SplitText.create(title, {
          type: 'lines',
          mask: 'lines',
          autoSplit: true,
          onSplit: (self) =>
            gsap.from(self.lines, {
              yPercent: 105,
              stagger: 0.08,
              duration: DURATION.cinematic,
              scrollTrigger: trigger,
            }),
        })
      }
    },
    { scope: sectionRef, dependencies: [introStarted, reducedMotion] },
  )

  const highlight = useCallback((id: MarketId | null) => {
    control.current.hovered = id
    control.current.invalidate?.()
  }, [])

  const togglePin = (id: MarketId) => {
    const next = control.current.pinned === id ? null : id
    control.current.pinned = next
    control.current.invalidate?.()
    setPinned(next)
  }

  // A beacon under the pointer lights its row too (no React render per frame).
  const onBeaconHover = useCallback((id: MarketId | null) => {
    for (const [rowId, row] of rowRefs.current) row.toggleAttribute('data-glow', rowId === id)
  }, [])

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, t: performance.now() }
    // A touch starts as a possible page scroll. Only a horizontal gesture takes the globe.
    if (event.pointerType === 'mouse') {
      event.currentTarget.setPointerCapture(event.pointerId)
      control.current.dragging = true
      control.current.velocity = 0
    }
  }

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (event.pointerId !== d.id) return
    const now = performance.now()
    const dx = event.clientX - d.x
    const dy = event.clientY - d.y
    if (!control.current.dragging) {
      if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return
      event.currentTarget.setPointerCapture(event.pointerId)
      control.current.dragging = true
      control.current.velocity = 0
    }
    const dt = Math.max(now - d.t, 1) / 1000
    control.current.dragX += dx
    control.current.dragY += dy
    control.current.velocity = control.current.velocity * 0.6 + (dx / dt) * 0.4
    drag.current = { id: d.id, x: event.clientX, y: event.clientY, t: now }
    control.current.invalidate?.()
  }

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerId !== drag.current.id) return
    control.current.dragging = false
    // A finger or mouse that came to rest before letting go should not fling the globe.
    if (reducedMotion || performance.now() - drag.current.t > 90) control.current.velocity = 0
    drag.current.id = -1
  }

  const statusOf = (wave: number) => styles[`wave${wave}`]

  return (
    <section
      id="markets"
      ref={sectionRef}
      aria-labelledby="markets-title"
      className={cx(
        styles.root,
        reducedMotion ? styles.still : styles.live,
        compact && styles.compact,
      )}
      data-theme="light"
    >
      <div ref={trackRef} className={styles.track}>
        <div className={styles.stage}>
          <PrismLight className={styles.prism} placement="right" intensity={0.65} />
          <div className={styles.copy}>
            <Eyebrow data-reveal>{markets.eyebrow}</Eyebrow>
            <h2 id="markets-title" className={styles.title} data-title>
              <Segments segments={markets.title} />
            </h2>
            <p className={styles.lead} data-reveal>
              {markets.lead}
            </p>
          </div>

          <div
            ref={globeRef}
            className={styles.globe}
            data-cursor="drag"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <SceneCanvas
              id="markets"
              label={markets.sceneLabel}
              toneMapping="neutral"
              camera={{ fov: 30, position: [0, 0.15, 5.4], near: 0.1, far: 50 }}
              onCreated={({ camera }) => {
                camera.lookAt(0, -0.22, 0)
              }}
            >
              <GlobeScene
                progress={progress}
                control={control}
                reducedMotion={reducedMotion}
                onBeaconHover={onBeaconHover}
                tag={tagRef}
                onFocusChange={setTagId}
              />
            </SceneCanvas>
            <div ref={tagRef} className={styles.tag} aria-hidden="true">
              {tagMarket ? (
                <span className={styles.tagInner}>
                  <span className={styles.tagCity}>{tagMarket.city}</span>
                  <span className={styles.tagStatus}>{tagMarket.statusLabel}</span>
                </span>
              ) : null}
            </div>
          </div>

          <ol className={styles.list} role="list">
            {markets.list.map((market) => (
              <li key={market.id} data-reveal>
                <button
                  type="button"
                  ref={(node) => {
                    if (node) rowRefs.current.set(market.id, node)
                    else rowRefs.current.delete(market.id)
                  }}
                  className={styles.row}
                  aria-pressed={pinned === market.id}
                  data-cursor="link"
                  onPointerEnter={() => highlight(market.id)}
                  onPointerLeave={() => highlight(null)}
                  onFocus={() => highlight(market.id)}
                  onBlur={() => highlight(null)}
                  onClick={() => togglePin(market.id)}
                >
                  <span className={cx(styles.dot, statusOf(market.wave))} aria-hidden="true" />
                  <span className={styles.country}>{market.country}</span>
                  <span className={styles.city}>{market.city}</span>
                  <span className={cx(styles.status, statusOf(market.wave))}>
                    {market.statusLabel}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}
